/**
 * Cleanup & Reclassify Helper - Expose utility functions to browser console
 *
 * Usage in browser console:
 *
 * // Cleanup old duplicate transactions
 * await window.cleanupOldTransactions(true)  // Dry run
 * await window.cleanupOldTransactions(false, true)  // Actual deletion
 *
 * // Reclassify transactions
 * await window.reclassifyTransactions(true, false)  // Dry run (all transactions)
 * await window.reclassifyTransactions(false, false)  // Actual reclassification (all transactions)
 * await window.reclassifyTransactions(false, true)  // Only unclassified transactions
 *
 * // Migrate merchant field (Issue #85) - CRITICAL FIX
 * await window.migrateMerchantField(true)  // Dry run
 * await window.migrateMerchantField(false, true)  // Actual migration
 *
 * // Cleanup misclassified rules (Issue #85)
 * await window.cleanupMisclassifiedRules('YCgCxDyZTijuOrfsVUBT', true)  // Dry run
 * await window.cleanupMisclassifiedRules('YCgCxDyZTijuOrfsVUBT', false, true)  // Actual deletion
 *
 * // Check for duplicate transaction types (auto-detects logged-in user)
 * await window.checkDuplicateTypes()  // Check for current user
 * await window.checkDuplicateTypes('specific-user-id')  // Check for specific user
 *
 * // Remove duplicate transaction types
 * await window.removeDuplicateTypes()  // Dry run for current user
 * await window.removeDuplicateTypes(undefined, false)  // Actual deletion for current user
 *
 * // Migrate missing system transaction types
 * await window.migrateMissingTransactionTypes()  // Dry run (shows what would be created)
 * await window.migrateMissingTransactionTypes(undefined, false)  // Actual creation
 *
 * Implementation lives in `./cleanup-helpers/`:
 *   - types.ts                — shared response shapes
 *   - callable-actions.ts     — Cloud Function wrappers that mutate data
 *   - callable-reports.ts     — Cloud Function wrappers that produce reports
 *   - firestore-diagnostics.ts — direct Firestore reads for diagnostics
 */

import {
  cleanupOldTransactions,
  reclassifyTransactions,
  migrateMerchantField,
  cleanupMisclassifiedRules,
  createRulesFromSuggestions,
  migrateInternalTransferTransactions,
  backfillTransactionHash,
} from './cleanup-helpers/callable-actions';
import {
  debugCategoryStructure,
  healthCheck,
  generateRuleSuggestions,
} from './cleanup-helpers/callable-reports';
import {
  diagnoseTxRules,
  diagnoseCategories,
  diagnoseTransactions,
  diagnoseRulesByCategory,
} from './cleanup-helpers/firestore-diagnostics';
import {
  checkDuplicateTypes,
  removeDuplicateTypes,
  listAllTypes,
} from './cleanup-duplicate-types';
import { migrateMissingTransactionTypes } from './migrate-missing-transaction-types';

export {
  cleanupOldTransactions,
  reclassifyTransactions,
  migrateMerchantField,
  cleanupMisclassifiedRules,
  createRulesFromSuggestions,
  migrateInternalTransferTransactions,
  backfillTransactionHash,
  debugCategoryStructure,
  healthCheck,
  generateRuleSuggestions,
  diagnoseTxRules,
  diagnoseCategories,
  diagnoseTransactions,
  diagnoseRulesByCategory,
};
export type { DuplicateGroup } from './cleanup-duplicate-types';

// Expose to window for console access
declare global {
  interface Window {
    cleanupOldTransactions: typeof cleanupOldTransactions;
    reclassifyTransactions: typeof reclassifyTransactions;
    migrateMerchantField: typeof migrateMerchantField;
    cleanupMisclassifiedRules: typeof cleanupMisclassifiedRules;
    healthCheck: typeof healthCheck;
    diagnoseTxRules: typeof diagnoseTxRules;
    diagnoseCategories: typeof diagnoseCategories;
    diagnoseTransactions: typeof diagnoseTransactions;
    diagnoseRulesByCategory: typeof diagnoseRulesByCategory;
    debugCategoryStructure: typeof debugCategoryStructure;
    generateRuleSuggestions: typeof generateRuleSuggestions;
    createRulesFromSuggestions: typeof createRulesFromSuggestions;
    migrateInternalTransferTransactions: typeof migrateInternalTransferTransactions;
    backfillTransactionHash: typeof backfillTransactionHash;
    listAllTypes: typeof listAllTypes;
    checkDuplicateTypes: typeof checkDuplicateTypes;
    removeDuplicateTypes: typeof removeDuplicateTypes;
    migrateMissingTransactionTypes: typeof migrateMissingTransactionTypes;
  }
}

// Initialize immediately when module loads (production only)
// Note: Admin utility functions are attached to window for production maintenance
// Console output is suppressed to avoid cluttering user's console
(function initializeHelpers() {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) return; // Skip in development - use imports directly

  window.cleanupOldTransactions = cleanupOldTransactions;
  window.reclassifyTransactions = reclassifyTransactions;
  window.migrateMerchantField = migrateMerchantField;
  window.cleanupMisclassifiedRules = cleanupMisclassifiedRules;
  window.healthCheck = healthCheck;
  window.diagnoseTxRules = diagnoseTxRules;
  window.diagnoseCategories = diagnoseCategories;
  window.diagnoseTransactions = diagnoseTransactions;
  window.diagnoseRulesByCategory = diagnoseRulesByCategory;
  window.debugCategoryStructure = debugCategoryStructure;
  window.generateRuleSuggestions = generateRuleSuggestions;
  window.createRulesFromSuggestions = createRulesFromSuggestions;
  window.migrateInternalTransferTransactions = migrateInternalTransferTransactions;
  window.backfillTransactionHash = backfillTransactionHash;
  window.listAllTypes = listAllTypes;
  window.checkDuplicateTypes = checkDuplicateTypes;
  window.removeDuplicateTypes = removeDuplicateTypes;
  window.migrateMissingTransactionTypes = migrateMissingTransactionTypes;

  // Admin helpers available - run window.healthCheck() to see all commands
})();
